import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { AI_PATH } from '@app/shared/constants/api-route.config';
import type { ChatModelOption, ChatStreamEvent } from '@app/shared/models/ai-chat.model';
import type { ListResponse } from '@app/shared/services/mixins/crud.service';
import { RestService } from '@app/shared/services/rest.service';

@Injectable({
    providedIn: 'root',
})
export class AiChatService {
    private readonly api = inject(RestService);
    private readonly authService = inject(AuthenticationService);
    getModels(): Observable<ListResponse<ChatModelOption>> {
        return this.api.get<ListResponse<ChatModelOption>>(`${AI_PATH}chat/models`);
    }

    stream(message: string, provider?: string, model?: string): Observable<ChatStreamEvent> {
        return new Observable<ChatStreamEvent>(subscriber => {
            const controller = new AbortController();

            const run = async (): Promise<void> => {
                const response = await fetch(`${this.api.baseUrl()}${AI_PATH}chat/stream`, {
                    method: 'POST',
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `JWT ${this.authService.getToken()}`,
                        'x-language': navigator.language,
                    },
                    // Undefined provider/model are dropped by JSON.stringify -> backend falls back.
                    body: JSON.stringify({ message, provider, model }),
                });

                if (!response.ok || !response.body) {
                    throw new Error(`Chat request failed (${response.status})`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let streamDone = false;

                // SSE frames are delimited by a blank line ("\n\n"); each frame is `data: <json>`.
                let result = await reader.read();
                while (!result.done && !streamDone) {
                    buffer += decoder.decode(result.value, { stream: true });

                    let delimiter = buffer.indexOf('\n\n');
                    while (delimiter !== -1) {
                        const frame = buffer.slice(0, delimiter);
                        buffer = buffer.slice(delimiter + 2);
                        const event = this.parseFrame(frame);
                        if (event) {
                            subscriber.next(event);
                            if (event.type === 'done') {
                                streamDone = true;
                                break;
                            }
                        }
                        delimiter = buffer.indexOf('\n\n');
                    }
                    if (streamDone) break;
                    result = await reader.read();
                }
                subscriber.complete();
            };

            run().catch((err: unknown) => {
                // An abort is our own teardown, not a failure.
                if (controller.signal.aborted) return;
                subscriber.error(err);
            });

            return () => controller.abort();
        });
    }

    /** Parse one SSE frame (its `data:` lines) into a `ChatStreamEvent`, or `null` if unparseable. */
    private parseFrame(frame: string): ChatStreamEvent | null {
        const data = frame
            .split('\n')
            .filter(line => line.startsWith('data:'))
            .map(line => line.slice('data:'.length).trim())
            .join('');
        if (!data) {
            return null;
        }
        try {
            return JSON.parse(data) as ChatStreamEvent;
        } catch {
            return null;
        }
    }
}
